import { Card } from "@/components/ui/card";
import type { UnifiedQuoteRow } from "@/hooks/useDirectQuoteRequests";
import { BarChart3 } from "lucide-react";

interface Props {
  rows: UnifiedQuoteRow[];
}

/**
 * Right-side analytics panel for the shipper "My Quotes" page.
 * STRICT: real data only. Empty states everywhere else. No fabricated numbers,
 * no placeholder point series shown as if real, no deltas without prior period.
 * // TODO: wire prior-period comparisons + activity/response-time time series.
 */
export function QuoteAnalyticsPanel({ rows }: Props) {
  const hasAny = rows.length > 0;
  const rowsWithResp = rows.filter((r) => r.response_count > 0);
  const hasResponses = rowsWithResp.length > 0;

  const avgLow =
    rowsWithResp.length > 0
      ? rowsWithResp.reduce((s, r) => s + (r.low ?? 0), 0) / rowsWithResp.length
      : null;

  const avgSpreadPct = (() => {
    const withBoth = rowsWithResp.filter((r) => r.low != null && r.high != null && (r.low as number) > 0);
    if (withBoth.length === 0) return null;
    const pcts = withBoth.map((r) => (((r.high as number) - (r.low as number)) / (r.low as number)) * 100);
    return pcts.reduce((a, b) => a + b, 0) / pcts.length;
  })();

  const totalQuotes = rows.reduce((s, r) => s + (r.response_count || 0), 0);

  // Top lanes: real only. If <5 exist, show what exists. Do NOT pad.
  const laneMap = new Map<string, { o: string; d: string; count: number }>();
  rows.forEach((r) => {
    const key = `${r.origin_city},${r.origin_state}→${r.destination_city},${r.destination_state}`;
    const cur = laneMap.get(key);
    if (cur) cur.count += 1;
    else laneMap.set(key, {
      o: `${r.origin_city}, ${r.origin_state}`,
      d: `${r.destination_city}, ${r.destination_state}`,
      count: 1,
    });
  });
  const topLanes = Array.from(laneMap.values()).sort((a, b) => b.count - a.count).slice(0, 5);
  const maxLane = topLanes[0]?.count ?? 0;

  return (
    <aside className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-foreground">
          Quote Analytics
          <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">(Last 30 Days)</span>
        </h2>
      </div>

      {/* Two mini cards */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Avg Low Quote
          </div>
          <div className="mt-1.5 text-lg font-bold text-foreground">
            {avgLow != null ? `$${Math.round(avgLow).toLocaleString()}` : "—"}
          </div>
          {!avgLow && (
            <div className="mt-1 text-[10px] text-muted-foreground">No data yet</div>
          )}
        </Card>
        <Card className="p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Avg Spread
          </div>
          <div className="mt-1.5 text-lg font-bold text-foreground">
            {avgSpreadPct != null ? `${avgSpreadPct.toFixed(1)}%` : "—"}
          </div>
          {avgSpreadPct == null && (
            <div className="mt-1 text-[10px] text-muted-foreground">No data yet</div>
          )}
        </Card>
      </div>

      {/* Quote Activity */}
      <Card className="p-4">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Total Quotes Received
        </div>
        <div className="mt-1 text-2xl font-bold text-foreground">
          {totalQuotes.toLocaleString()}
        </div>
        {!hasResponses ? (
          <div className="mt-3 flex flex-col items-center justify-center py-6 text-center border border-dashed rounded-md">
            <BarChart3 className="h-6 w-6 text-muted-foreground/40 mb-1" />
            <div className="text-xs text-muted-foreground">No quote activity yet</div>
            <div className="text-[11px] text-muted-foreground/70 mt-0.5">
              Broker responses will chart here
            </div>
          </div>
        ) : (
          // Minimal real-data bar sparkline: one bar per row (response_count).
          // // TODO: wire proper date-bucketed activity series.
          <div className="mt-3 flex items-end gap-[3px] h-16">
            {rows.slice().reverse().slice(0, 30).map((r, i) => {
              const max = Math.max(...rows.map((x) => x.response_count || 0), 1);
              const h = Math.max(2, Math.round(((r.response_count || 0) / max) * 60));
              return (
                <div
                  key={`${r.id}-${i}`}
                  className="flex-1 rounded-sm bg-brand-teal/70"
                  style={{ height: `${h}px` }}
                  title={`${r.response_count} quotes`}
                />
              );
            })}
          </div>
        )}
      </Card>

      {/* Top Lanes */}
      <Card className="p-4">
        <div className="text-sm font-semibold text-foreground mb-3">Top Lanes by Quote Volume</div>
        {topLanes.length === 0 ? (
          <div className="text-xs text-muted-foreground py-4 text-center border border-dashed rounded-md">
            No lanes yet — post your first quote to start tracking
          </div>
        ) : (
          <div className="space-y-2.5">
            {topLanes.map((l) => (
              <div key={`${l.o}-${l.d}`} className="text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium text-foreground">
                    {l.o} → {l.d}
                  </span>
                  <span className="text-muted-foreground shrink-0">
                    {l.count} {l.count === 1 ? "request" : "requests"}
                  </span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-brand-teal"
                    style={{ width: `${maxLane ? (l.count / maxLane) * 100 : 0}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {!hasAny && (

        <Card className="p-4 bg-brand-teal/5 border-brand-teal/30">
          <div className="text-xs text-foreground">
            <span className="font-semibold">No quote data yet.</span>{" "}
            <span className="text-muted-foreground">
              Post your first quote to start tracking pricing, spread, and lane volume.
            </span>
          </div>
        </Card>
      )}
    </aside>
  );
}

export default QuoteAnalyticsPanel;
